import { Graphics } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import { CollisionWorld, stepPlayerMotion } from "../../../game/player-motion";
import {
  StoreLayout,
  fixtureStandCell,
  registerStandCell,
} from "../../../game/store-layout";
import { Vector } from "../../../game/Vectors";
import { cellToPixel, PIXELS_PER_CELL } from "../../../views/shop-scale";
import { ACTORS_Z } from "../../../views/z-order";

/**
 * The other people in the store: purely presentational walkers, like
 * the sidewalk's customers back home (CustomerEntity/CustomerView) but
 * on the sales floor — one entity per walker, spawned as the scene
 * root's children so they live and die with the venue. Each browses a
 * patrol of shelves, pauses at each, and yields to the player — and
 * they're solid to the walking body (the scene root reads their
 * positions into its collision world), because shouldering through a
 * person reads wrong where sliding along a rack reads fine.
 *
 * Nothing here touches GameState: positions live in the entities and
 * die with the scene. Movement is the same collision step the player
 * walks with — a shopper aimed across an island slides around its
 * corner the way a body does — and one who stays wedged too long just
 * moves on to the next stop on the list instead of milling against the
 * racking.
 */

export const SHOPPER_RADIUS = 0.55;

/** A window-shopping stroll, in cells per second. */
const SHOPPER_SPEED = 2.4;

/** How close the player has to be for a shopper to stop and yield. */
const YIELD_DISTANCE = 2.1;

/** How long a shopper browses a shelf before moving on, in seconds. */
const BROWSE_SECONDS = 3.2;

/** Give up on a leg after this long — wedged on a corner, or yielding
 * to a player who parked in the aisle. */
const LEG_TIMEOUT_SECONDS = 12;

const SHIRT_COLORS = [0x9c6b53, 0x53759c, 0x6f9c53, 0x8a539c, 0x9c8f53];
const BODY_RADIUS = PIXELS_PER_CELL * 0.34;

/** What a shopper reads off the venue it walks: where the player's
 * body is, and the floor's collision world. The scene root provides
 * both. */
export interface ShopperVenue {
  bodyPosition(): Vector | null;
  walkWorld(): CollisionWorld | null;
}

/** One shopper's dealt patrol. */
export interface ShopperPatrol {
  start: Vector;
  /** The patrol's stops, walked in order, wrapping. */
  stops: ReadonlyArray<Vector>;
  firstStop: number;
  browseLeft: number;
  /** Stable cosmetic tint index. */
  hue: number;
}

/** Deterministic tiny PRNG so a store's crowd is stable per layout. */
function seeded(seed: number): () => number {
  let state = seed || 1;
  return () => {
    state = (state * 1103515245 + 12345) % 2147483648;
    return state / 2147483648;
  };
}

/** Deal the store's crowd: each shopper a shuffled walk of the store's
 * stands, starting somewhere different from the others. */
export function shopperPatrols(layout: StoreLayout): ShopperPatrol[] {
  const spots = [
    ...layout.fixtures.map((fixture) => fixtureStandCell(fixture)),
    registerStandCell(layout),
  ].map(([x, y]): Vector => [x + 0.5, y + 0.5]);
  if (spots.length < 2) {
    return [];
  }
  const count = Math.min(3, Math.floor(spots.length / 4));
  const rng = seeded(layout.fixtures.length * 31 + layout.interior[0]);
  return Array.from({ length: count }, (_, index) => {
    const patrol = [...spots].sort(() => rng() - 0.5);
    return {
      start: patrol[0],
      stops: patrol,
      firstStop: 1 % patrol.length,
      browseLeft: rng() * BROWSE_SECONDS,
      hue: index,
    };
  });
}

export class StoreShopper extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Level;
  pausable = false;

  position: Vector;
  private stops: ReadonlyArray<Vector>;
  private stopIndex: number;
  /** Seconds of browsing left at the current stop; walking when 0. */
  private browseLeft: number;
  /** Seconds spent on the current leg, for the wedge bail-out. */
  private legSeconds = 0;

  private body: Graphics & GameSprite;

  constructor(
    private readonly venue: ShopperVenue,
    patrol: ShopperPatrol,
  ) {
    super();
    this.position = patrol.start;
    this.stops = patrol.stops;
    this.stopIndex = patrol.firstStop;
    this.browseLeft = patrol.browseLeft;

    // Ground shadow, body, head — the sidewalk customers' exact read.
    this.body = new Graphics() as Graphics & GameSprite;
    this.body.layerName = "actors";
    this.body.zIndex = ACTORS_Z.storeShopper;
    this.body.ellipse(
      0,
      BODY_RADIUS * 0.35,
      BODY_RADIUS * 1.1,
      BODY_RADIUS * 0.55,
    );
    this.body.fill({ color: 0x000000, alpha: 0.18 });
    this.body.circle(0, 0, BODY_RADIUS);
    this.body.fill(SHIRT_COLORS[patrol.hue % SHIRT_COLORS.length]);
    this.body.circle(0, 0, BODY_RADIUS * 0.5);
    this.body.fill(0xd9b38c);
    this.sprite = this.body;
  }

  @on("tick")
  onTick(dt: number) {
    const playerPosition = this.venue.bodyPosition();
    const world = this.venue.walkWorld();
    if (!playerPosition || !world) return;

    if (this.browseLeft > 0) {
      this.browseLeft = Math.max(0, this.browseLeft - dt);
      return;
    }
    const target = this.stops[this.stopIndex];
    const dx = target[0] - this.position[0];
    const dy = target[1] - this.position[1];
    const distance = Math.hypot(dx, dy);
    if (distance < 0.15) {
      this.stopIndex = (this.stopIndex + 1) % this.stops.length;
      this.browseLeft = BROWSE_SECONDS;
      this.legSeconds = 0;
      return;
    }
    this.legSeconds += dt;
    if (this.legSeconds > LEG_TIMEOUT_SECONDS) {
      // Wedged or blocked long enough — skip the stop rather than mill
      // against whatever is in the way.
      this.stopIndex = (this.stopIndex + 1) % this.stops.length;
      this.legSeconds = 0;
      return;
    }
    // Yield to the player: stop and wait while they're close ahead.
    const playerDistance = Math.hypot(
      playerPosition[0] - this.position[0],
      playerPosition[1] - this.position[1],
    );
    if (playerDistance < YIELD_DISTANCE) {
      return;
    }
    this.position = stepPlayerMotion(
      this.position,
      [dx / distance, dy / distance],
      Math.min(SHOPPER_SPEED, distance / dt || SHOPPER_SPEED),
      dt,
      world,
      SHOPPER_RADIUS,
    );
  }

  @on("render")
  onRender() {
    this.body.position.set(
      cellToPixel(this.position[0]),
      cellToPixel(this.position[1]),
    );
  }
}
